// Bundle = artifact xác định sau pha học. Không còn ngôn ngữ tự nhiên ở phần điều hướng.
import type { Classify, Generate, Interrupt, Knowledge, Param, Script, Step } from "./dsl/schema";

export interface Viewport { w: number; h: number }

export type Rung =
  // text: chữ của phần tử lúc học — nấc vị trí (css đường dẫn, xpath, geometry) phải khớp chữ này, chống click nhầm
  | { t: "css"; sel: string; text?: string }
  | { t: "text"; text: string; tag?: string; exact?: boolean }
  | { t: "xpath"; xpath: string; text?: string }
  | { t: "geometry"; anchors: { text: string; dx: number; dy: number }[]; tag?: string; text?: string; viewport: Viewport }
  | { t: "model"; prompt: string; tag?: string };

export const RUNG_ORDER: Rung["t"][] = ["css", "text", "xpath", "geometry", "model"];

// Phần tử nằm trong khung nhúng (iframe): url = origin + pathname của tài liệu trong khung lúc dạy.
export interface FrameHint { url: string; name?: string }

export interface Locator {
  describe: string;
  ladder: Rung[];
  // "item": thang tương đối trong phần tử mục hiện tại của for_each (danh sách đã extract)
  within?: "item";
  // "bai" (L-42): phần tử phải thuộc BÀI đang làm — trong hộp thoại bài đang mở, không có thì trong mục hiện tại (for_each), không có mục thì
  // trong nội dung chính (main/article). Mọi nấc (css/text/xpath/hình học/tự vá) đều lọc theo đây. Phần tử khung chat/tin nhắn thì
  // LUÔN bị loại, có hay không có trường này (src/content/o-chat.ts).
  // "tao_bai" (2026-10-02): phần tử phải nằm TRONG hộp thoại soạn bài mới đang hiện (tiêu đề "Tạo bài viết"/"Create post", đọc từ aria-label /
  // aria-labelledby / tiêu đề trong hộp) — hộp chưa vẽ thì không nhận ô soạn nào khác trên trang (ô bình luận, hộp thoại bài khác, chat).
  // Bản plugin cũ không biết giá trị này thì bỏ qua (như không có vùng) — thang của khối vẫn chỉ gồm nấc trong hộp thoại.
  // "bai_dich" (04/10/2026, trang MỘT bài mở theo địa chỉ): phần tử phải thuộc ĐÚNG bài đang mở — hộp thoại bài nếu có (không lùi ra nền), không có thì bài
  // ĐẦU TIÊN của trang (bài gợi ý bên dưới không tính); không nhận ra bài nào → không nhận gì (src/content/dom.ts khungBaiDich). Plugin cũ không biết
  // giá trị này thì coi như KHÔNG có vùng → bundle dùng nó tự khai can_plugin (src/shared/phien-ban-plugin.ts PLUGIN_CAN_BAI_DICH).
  // "tin_nhan" (07/10/2026, nhóm 4 trả lời tin nhắn): CHỈ trên trang hộp thư (/messages…) mới nhận phần tử — kể cả khung chat; trang khác không nhận gì.
  vung?: "bai" | "tao_bai" | "bai_dich" | "tin_nhan";
  // (2026-10-02) Phần tử thuộc bình luận/bài của CHÍNH nick đang đăng nhập (tác giả của [role=article] gần nhất = nick, src/content/nick.ts) →
  // KHÔNG thao tác (bot không tự trả lời / tự thích chính mình). Không đọc được nick → thao tác như cũ. Bản plugin cũ bỏ qua trường này.
  khong_cua_nick?: boolean;
  frame?: FrameHint;
}

export interface Within { sel: string; index: number }

// CHỐT (2026-10-02): điều kiện content script kiểm NGAY TRƯỚC khi thao tác có tác dụng — click: trước cú bấm; type: trước khi gõ VÀ (có enter)
// trước phím Enter. Không đạt → không bấm/gõ/Enter, thao tác báo lỗi kèm ly_do (bước thất bại, không bấm lại). Dùng cho trang SPA tự đổi nội dung
// giữa các thao tác của một bước: TikTok/YouTube TỰ CHUYỂN sang video khác trong lúc mở bảng bình luận → gõ → Đăng (bình luận nhầm video).
// Bản cũ không biết trường này thì bỏ qua (passthrough) — thao tác chạy như trước.
export interface Chot { cond: Cond; ly_do: string }

export type Action =
  | { kind: "click"; target: Locator; chot?: Chot }
  // sensitive: gõ vào ô mật khẩu lúc dạy → compiler đổi thành {{secrets.X}} và lưu giá trị vào máy, không vào bundle.
  | { kind: "type"; target: Locator; text: string; clear?: boolean; enter?: boolean; sensitive?: boolean; chot?: Chot }
  | { kind: "select"; target: Locator; value: string }
  | { kind: "press"; key: string }
  | { kind: "navigate"; url: string }
  // trusted: lăn chuột THẬT qua chrome.debugger (Input.dispatchMouseEvent mouseWheel) — cho trang chỉ tải nội dung khi có cử chỉ người dùng (TikTok).
  | { kind: "scroll"; target?: Locator; dy?: number; trusted?: boolean };

// Selector đặc biệt "ô nhập chữ ĐANG được focus" (textarea / contenteditable), xuyên shadow DOM. Content script KHÔNG giải bằng CSS :focus
// (Chrome không khớp :focus khi cửa sổ không có tiêu điểm hệ thống) mà theo chuỗi document.activeElement → shadowRoot.activeElement.
// Dùng cho khảo sát Reddit gõ nháp: ô soạn đã mở rộng nằm trong shadow DOM, chưa đo được cấu trúc.
export const SEL_O_DANG_GO = ':focus:is(textarea, [contenteditable="true"])';

export type Cond =
  | { t: "text_visible"; text: string }
  | { t: "text_gone"; text: string }
  | { t: "locator_visible"; target: Locator }
  | { t: "locator_gone"; target: Locator }
  | { t: "locator_disabled"; target: Locator }
  | { t: "dom_exists"; sel: string }
  | { t: "dom_gone"; sel: string }
  | { t: "url_contains"; s: string }
  | { t: "value_equals"; target: Locator; value: string }
  | { t: "download"; glob: string }
  | { t: "api"; url: string; method: "GET" | "POST"; body?: unknown; expect_status?: number; json_path?: string; equals?: unknown; contains?: string };

export interface ExtractField {
  sel: string | null;
  attr: "text" | "href" | "value" | "src" | "html" | `data-${string}` | "title" | "id" | "alt" | "aria-label" | "anh_lon"; // anh_lon: URL ảnh lớn nhất trong mục (bỏ avatar/icon <80px) — AI nhìn khi chữ ngắn
  regex?: string;
  // Selector dự phòng (thử lần lượt khi sel không khớp): trang như Facebook đổi cấu trúc từng mục.
  alt?: string[];
  // Không selector nào khớp → lấy khối chữ dài nhất trong mục (chỉ cho trường nội dung). Kết quả ghi nhận là "dự phòng".
  fallback?: "longest_text";
  // PHÂN LOẠI giá trị đã đọc thành nhãn cố định (vd. chữ cuối thẻ nhóm "Tham gia" / "Đang chờ xem xét" → chua_tham_gia / cho_duyet):
  // regex đầu tiên khớp quyết định; không khớp cái nào → mac_dinh (không có → null). Áp SAU regex của trường.
  phan_loai?: { re: string; gt: string }[];
  mac_dinh?: string;
}

// Một nhánh kiểm trang: điều kiện `khi` đúng → ghi `ghi` vào nhật ký, gán các trường `gan` vào MỤC hiện tại (for_each).
export interface NhanhKiem { khi: Cond; ghi: string; gan?: Record<string, string> }
// Nhánh kết quả SAU thao tác (thay cho hậu kiểm một-đường): nhánh đầu tiên đạt quyết kết quả. ket "can_nguoi" = việc đã chạm tới chỗ
// máy không được tự làm (vd. nhóm hỏi câu hỏi kiểm duyệt) → bỏ mục, ghi rõ cần người; `dong` = nút đóng hộp thoại đó (không trả lời gì).
// tra_loi (05/10/2026, src/shared/cau-hoi-nhom.ts): nhánh hộp câu hỏi kiểm duyệt — bat = "co" (sau khi lắp) → runner TỰ trả lời (AI) rồi Gửi thay vì đóng;
// không trả lời được (thông tin cá nhân bắt buộc, AI hỏng, người duyệt từ chối…) → đóng như cũ. Plugin cũ bỏ qua khoá lạ (= đóng như cũ) → can_plugin.
export interface NhanhKetQua extends NhanhKiem { ket: "ok" | "can_nguoi"; dong?: Locator; tra_loi?: { bat: string } }
// LƯỚT BẢNG TIN như người (src/background/runner.ts runLuot): mở trang (url, không có thì một nhóm "da_tham_gia" trong danh sách
// nguon_nhom, không có nữa thì trang_chu), cuộn từng đoạn ngẫu nhiên, dừng đọc theo độ dài bài, đôi khi cuộn ngược, trong `giay` giây
// hoặc tới khi thấy `so_bai` bài; gom bài vào `into` (extract_spec của bước) và đánh dấu ngẫu nhiên tối đa `so_thich` bài (chon_thich).
// Các giá trị là chữ có thể chứa {{…}} (tham số kịch bản) — runner thay lúc chạy.
// nhom_da_vao + tu_khoa_nhom_da_vao: danh sách nhóm vừa tìm không có nhóm đã vào → mở trang "Nhóm của bạn" (nhom_da_vao), chọn nhóm đã tham
// gia SẴN có tên khớp từ khoá (src/shared/luot-nhom.ts), không khớp thì mới về trang_chu.
// chi_nhom_da_vao (05/10/2026 — "bình luận một bài" Facebook để trống địa chỉ, src/shared/fb-khoi.ts): CHỈ lướt một nhóm nick ĐÃ THAM GIA có tên khớp từ khoá
// (trang "Nhóm của bạn"; từ khoá bỏ hư từ như tự chọn nhóm đăng) — không lướt nhóm đang chọn để đăng, không lùi về trang chủ; không có nhóm hợp → danh
// sách RỖNG + lý do (lần chạy không có việc, không dừng); trang đăng nhập / xác minh → bước lỗi như mọi bước lướt. Cần plugin ≥ 0.1.54 (bundle tự khai).
// (đợt B) phut_tu / phut_den: lướt NGẪU NHIÊN trong khoảng phút (thay `giay`; trần 30 phút như `giay`; không dừng sớm vì đủ so_bai); so_thich_tu: thích NGẪU NHIÊN [so_thich_tu, so_thich] bài.
export interface CauHinhLuot { url?: string; nguon_nhom?: string; trang_chu?: string; nhom_da_vao?: string; tu_khoa_nhom_da_vao?: string; giay?: string | number; so_bai?: string | number; so_thich?: string | number; tu_khoa_thich?: string; chi_nhom_da_vao?: boolean; phut_tu?: string | number; phut_den?: string | number; so_thich_tu?: string | number }

// Tự chọn nhóm để đăng (runner.ts runChonNhom). Các giá trị chữ có thể chứa {{…}} (tham số kịch bản) — runner thay lúc chạy.
export interface CauHinhChonNhom {
  url?: string;                     // địa chỉ nhóm Boss nhập; rỗng = tự chọn
  bien?: string;                    // biến lưu nhóm đã chọn {url, ten, ly_do} (mặc định nhom_chon)
  tu_khoa?: string;                 // từ khoá chọn nhóm (cách nhau dấu phẩy); rỗng = theo chu_de
  chu_de?: string;
  gian_cach_ngay?: string | number; // không đăng lại CÙNG nhóm trong số ngày này (mặc định 3; 0 = không giãn cách)
  nhom_da_vao?: string;             // trang "Nhóm của bạn" — chỉ mở khi dữ liệu bot không có nhóm hợp (đọc lại rồi báo Boss)
  toi_da_thu?: number;              // số nhóm mở ra kiểm ô đăng bài tối đa (mặc định 3)
  kiem_dang?: Locator;              // ô soạn bài của nhóm — không thấy = nhóm không cho đăng → báo Boss "bị chặn đăng", thử nhóm kế
}

export type ExtractSpec =
  // item_key: tên trường dùng làm khoá mục (vào sổ cái chống trùng). Cho phép DANH SÁCH ƯU TIÊN: lấy trường đầu tiên
  // có giá trị. Facebook cần vậy — kết quả tìm kiếm phần lớn có liên kết bài, nhưng không phải bài nào cũng có,
  // nên ưu tiên liên kết rồi mới lùi về nội dung.
  // bo_muc_rong: bỏ mục mà MỌI trường kể tên đều rỗng (khung chưa vẽ / bị làm rỗng của feed tải lười, ảo hoá — Facebook L-1). Mục bị bỏ không tính
  // vào "N mục"; _index của mục còn lại vẫn là vị trí THẬT trong danh sách (thao tác within: item không lệch sang mục khác).
  // cuon_gom: feed tải lười + ẢO HOÁ (Facebook chỉ giữ ~7 bài có chữ cùng lúc) — runner cuộn xuống và GOM DẦN mục qua nhiều vị trí cuộn tới khi đủ
  // `muc` mục có chữ, hoặc đã cuộn `lan` lần, hoặc 2 lần cuộn liền không thêm gì (src/shared/gom-dan.ts). Thao tác within: item trên danh sách này
  // cuộn KHUNG của mục vào màn hình trước (khung đã bị làm rỗng khi cuộn qua được vẽ lại).
  // bo_cua_nick (2026-10-02): bỏ mục mà TÁC GIẢ là nick đang đăng nhập (trường tên `ten`, trường liên kết hồ sơ `lien_ket`) — content tự đọc nick
  // trên trang (src/content/nick.ts). Không đọc được nick → không bỏ gì. Bản plugin cũ không biết khoá này thì bỏ qua (như trước).
  // bo_nick_da_phan_hoi (FB 2026-10-03): bỏ mục (bình luận) mà trong phần phản hồi của nó (selector `phan_hoi`, tương đối trong mục — các article phản
  // hồi) có phản hồi của CHÍNH nick đang đăng nhập → không trả lời trùng. Không đọc được nick → không bỏ gì. Bản plugin cũ bỏ qua khoá này.
  // mo_rong (FB 2026-10-03): TRƯỚC khi đọc danh sách, bấm nút `nut` ("Xem thêm bình luận / phản hồi") tối đa `lan` lần (chữ, có thể là {{…}}; 0 = không
  // bấm), mỗi lần chờ số phần tử `dem` (css; không có = chính danh sách) đổi; hết nút thì thôi. Thử định vị không bấm. Bản plugin cũ bỏ qua (chỉ đọc phần đang hiện).
  | { mode: "list"; list: Locator; fields: Record<string, ExtractField>; item_key?: string | string[]; bo_muc_rong?: string[]; cuon_gom?: { muc: number; lan: number }; bo_cua_nick?: { ten: string; lien_ket?: string }; bo_nick_da_phan_hoi?: { phan_hoi: string }; mo_rong?: { nut: Locator; lan: number | string; dem?: string } }
  | { mode: "single"; target: Locator; attr: ExtractField["attr"]; regex?: string };

export interface BundleStep {
  id: string;
  on?: string;
  do?: string;
  actions?: Action[];
  postcondition?: Cond[];
  expect?: string;
  expect_file?: string;
  risk: "low" | "high";
  timeout_ms: number;
  retry: { times: number; backoff_ms: number[]; only_if: ("network" | "timeout" | "not_found" | "any")[] };
  optional: boolean;
  idempotent?: "safe" | "guarded" | "unsafe";
  // (03/10/2026) Bước rủi ro cao người dùng đặt "Tự động (không cần duyệt)" trên Kịch bản sơ đồ: chạy thật không dừng hỏi (như rec.autoApprove
  // nhưng chỉ bước này); AI gác cổng "hoi_nguoi" vẫn hỏi; không người trực vẫn gửi. Chỉ đúng giá trị true mới tính. Plugin cũ bỏ qua → vẫn hỏi.
  auto_approve?: boolean;
  ledger?: string;
  when?: string;
  // Lý do DỄ HIỂU khi `when` không thoả, đồng thời khai rằng bỏ qua đó nghĩa là "lúc này không có gì để làm" (không phải lỗi/cảnh báo) — vd. bài
  // chưa ai trả lời. Bước rủi ro cao bị bỏ như vậy → RunRecord.khongCoViec, không cảnh báo "0 việc" (src/shared/ket-qua-chay.ts). Tuỳ chọn: bot cũ
  // (≤0.1.45) bỏ qua khoá lạ này (kiemBundle passthrough) và chạy như trước; Boss tự nhận ra từ bundle + nhật ký (chuanHoaKhongCoViec).
  ly_do_bo_qua?: string;
  for_each?: string;
  max_items?: number;
  on_item_error?: "skip" | "halt";
  steps?: BundleStep[];
  extract?: string;
  min_items?: number; // extract danh sách dưới ngưỡng → lỗi bước (xem schema.ts)
  extract_spec?: ExtractSpec;
  into?: string;
  filter?: string;
  classify?: Classify;
  template?: string;
  generate?: Generate;
  paginate?: { next: Locator; until: Cond; max_pages: number };
  wait_ms?: number;
  // Nghỉ NGẪU NHIÊN trong [wait_ms, wait_max_ms] (DSL `wait_max`) — bước "nghỉ" của sơ đồ. Không có → nghỉ đúng wait_ms như cũ.
  wait_max_ms?: number;
  // ---- phần đã học (learned) cho thao tác mạng xã hội nhiều nhánh ----
  bo_qua_khi?: NhanhKiem[]; // sau khi đã mở trang đích, TRƯỚC gác cổng/duyệt/thao tác: thấy → bỏ qua mục (không bấm gì)
  nhanh_ket_qua?: NhanhKetQua[]; // sau thao tác: chờ một trong các nhánh (thay postcondition)
  // bộ đếm hạn mức RIÊNG mỗi ngày (tham_gia_nhom: Cài đặt thamGiaNhomMoiNgay; 07/10/2026 thêm theo_doi / chia_se / dang_bai_kenh / tin_nhan / ho_so — src/shared/han-muc-ngay.ts)
  // — vẫn tính chung vào hạn mức ngày. Loại khác tham_gia_nhom → bundle khai can_plugin PLUGIN_CAN_KENH_MOI.
  dem_ngay?: import("./han-muc-ngay").LoaiDemNgay;
  // (07/10/2026, nhóm 3 "Đọc số liệu kênh") bước ĐẠT → runner gom các biến đã đọc thành RunRecord.soLieuKenh (src/shared/so-lieu-kenh.ts); 0 chỉ số → dừng có lý do.
  // Bundle có khoá này tự khai can_plugin PLUGIN_CAN_KENH_MOI (plugin cũ bỏ qua khoá lạ → không báo số liệu).
  bao_so_lieu?: import("./so-lieu-kenh").CauHinhBaoSoLieu;
  luot?: CauHinhLuot;
  // ---- 04/10/2026 (đăng nhóm Facebook để trống địa chỉ nhóm) — trong phần đã học, bản plugin cũ bỏ qua khoá lạ ----
  // chon_nhom: bước MỞ NHÓM của "đăng bài vào nhóm" — url có giá trị → mở như cũ (actions); RỖNG → bot tự chọn nhóm đã vào hợp từ khoá/chủ đề theo
  // dữ liệu nhóm của bot trên Boss (src/shared/nhom-fb.ts), kiểm nhóm có ô đăng bài, ghi vào biến `bien`; bước lướt đứng trước trong cùng kịch bản lướt
  // đúng nhóm đó. Bundle có chon_nhom rỗng tự khai can_plugin (src/shared/phien-ban-plugin.ts).
  chon_nhom?: CauHinhChonNhom;
  // ghi_nhom: { su_kien: "dang" } — bước đăng bài vào nhóm: báo Boss kết quả (đăng được / bị chặn / chờ admin duyệt / lỗi) cho nhóm vừa đăng.
  ghi_nhom?: { su_kien: "dang" };
  // ---- FB 2026-10-03 (trả lời bình luận dưới bài của mình / đăng bài lên Trang) — đều nằm trong phần đã học (learned), bản plugin cũ bỏ qua ----
  // dung_neu_sai: sau khi bước ĐẠT, từng biểu thức `dk` phải đúng — sai → DỪNG cả lần chạy với lý do `ly_do` (thay {{…}}), không thử lại / không hỏi AI.
  dung_neu_sai?: { dk: string; ly_do: string }[];
  // luon_chay: bước DỌN DẸP (chuyển về hồ sơ cá nhân sau khi đăng dưới tên Trang): lần chạy dừng/lỗi trước khi tới bước này thì VẪN chạy nó (một lần,
  // hỏng thì ghi lỗi, lý do dừng gốc giữ nguyên). KHÔNG chạy khi dừng vì captcha/checkpoint hay người dùng bấm Dừng.
  luon_chay?: boolean;
  // ly_do_bo: bước lọc ghi thêm "bỏ N mục: <lý do>" khi có mục bị bỏ (người đọc nhật ký biết vì sao).
  ly_do_bo?: string;
  // ghi_khi_rong: bước lấy danh sách ra 0 mục → ghi chú này (thay câu chung "trang có thể chưa đăng nhập…") — vd. "bài chưa có bình luận nào".
  ghi_khi_rong?: string;
  // khong_viec_khi_rong (L-97/L-106, 07/10/2026): bước lấy danh sách CỦA CHÍNH MÌNH (video / bình luận trên video của mình, bình luận dưới bài của mình) — ra
  // 0 mục nghĩa là "lúc này không có gì để làm" (không phải chưa đăng nhập): lần chạy SẠCH, RunRecord.khongCoViec = câu này (shared/ket-qua-chay.ts). Phần đã
  // học (learned); plugin cũ bỏ qua khoá lạ → cảnh báo như trước. Danh sách tìm kiếm công khai KHÔNG khai (0 mục vẫn cảnh báo "có thể chưa đăng nhập").
  khong_viec_khi_rong?: string;
  // gioi_han_luot: trần số mục LÀM ĐƯỢC của bước này cho CẢ lần chạy (cộng qua mọi vòng cha — nhiều bài × nhiều bình luận); max_items vẫn là trần mỗi vòng.
  // Chữ (có thể là {{…}}) — không phải số dương thì không áp.
  gioi_han_luot?: number | string;
  // (06/10/2026) cho_giua: CHỜ NGẪU NHIÊN [tu, den] GIÂY trước mỗi mục của vòng lặp (không chờ trước mục đầu / sau mục cuối) — src/shared/cho-giua-lan.ts. Số, chữ số hoặc {{tham_số}}
  // (render lúc chạy, kể cả tham số giao khi chạy). Có → thay nhịp "nghỉ giữa hai mục" và nhịp đăng chung cho các mục sau của vòng; không có → nhịp cũ. Bundle có khoá này khai can_plugin.
  cho_giua?: { tu: number | string; den: number | string };
  // ---- (08/10/2026) ĐỢT B — các khoá tuỳ chọn của bước (src/shared/tuy-chon-buoc.ts). Chỉ có trong bundle khi người dùng ĐẶT ô tương ứng trên sơ đồ (bỏ trống = bundle / hash
  // y như cũ); bundle có khoá nào dưới đây tự khai can_plugin PLUGIN_CAN_DOT_B (plugin cũ bỏ qua khoá lạ → làm khác ý). Giá trị là chữ số (đã thay lúc lắp) hoặc số.
  // han_ngay: hạn mức MỖI NGÀY riêng của bước này (thắng hạn của lịch và Cài đặt — han-muc-ngay.ts hanMucHieuLuc); loại đếm = dem_ngay hoặc suy từ sổ cái.
  han_ngay?: number | string;
  // nhac_sp (bước SOẠN câu bình luận/trả lời): ti_le = tỉ lệ nhắc sản phẩm (%) RIÊNG của bước (thắng Cài đặt tiLeNhacSanPham); bat_buoc = "co" → câu được bốc "có nhắc"
  // mà sau MỘT lần soạn lại vẫn thiếu tên sản phẩm thì BỎ mục (không gửi), thay vì giữ câu.
  nhac_sp?: { ti_le?: number | string; bat_buoc?: string };
  // xem_truoc (bước GỬI bình luận/trả lời trên một video/bài): mở trang đích rồi XEM / ĐỌC ngẫu nhiên [tu, den] giây trước khi gõ (Nháp chỉ ghi, không chờ).
  xem_truoc?: { tu: number | string; den: number | string };
  // khoang_nghi (bước wait của khối Nghỉ X / TikTok / YouTube / Reddit): nghỉ NGẪU NHIÊN [tu, den] giây thay cho số giây cố định của wait (như wait_max của Facebook).
  khoang_nghi?: { tu: number | string; den: number | string };
  // Vòng lặp (for_each) của bước LƯỚT: lap_theo_gio = lặp tới khi hết [tu, den] PHÚT (ngẫu nhiên; hết danh sách thì cuộn rồi đọc lại bằng bước `lay_lai`, danh sách
  // cố định thì quay vòng); so_muc = số mục ngẫu nhiên [tu, den] thay max_items; chon_thich = thích ngẫu nhiên [tu, den] mục (thay tỉ lệ thích); tim_khi_luot = thao tác
  // thả tim / thích cho mục được chọn (khối lướt KHÔNG có bước thích riêng — TikTok Dành cho bạn, YouTube Shorts).
  lap_theo_gio?: { tu: number | string; den: number | string; lay_lai?: string };
  so_muc?: { tu: number | string; den: number | string };
  chon_thich?: { tu: number | string; den: number | string };
  tim_khi_luot?: { actions: Action[]; postcondition?: Cond[]; bo_qua_khi?: NhanhKiem[]; ledger?: string; mo_ta?: string };
  // mo_truoc: mở địa chỉ này TRƯỚC khi chạy bước (vd X: tab "Dành cho bạn" trước khi đọc bảng tin). Chỉ địa chỉ thuộc trang đích.
  mo_truoc?: string;
  // (06/10/2026) ds_tu_khoa: bước LẤY DANH SÁCH sau bước tìm theo từ khoá nhiều dòng — param = tên tham số từ khoá (tu_khoa / tu_khoa_nhom), tim = id bước tìm.
  // Runner dùng để áp "số lượng" của từ khoá lên các vòng lặp trên danh sách này và để chạy tất cả từ khoá (src/shared/tu-khoa-nhieu-dong.ts). Bundle có khoá này khai can_plugin.
  ds_tu_khoa?: { param: string; tim: string };
  // chi_khi_xong: id bước (cùng cấp) phải ĐÃ THỰC SỰ LÀM ở lần chạy này thì bước này mới chạy (hậu kiểm sau khi đăng — Nháp không kiểm).
  chi_khi_xong?: string;
  // ---- 04/10/2026 (bình luận / trả lời KÈM ẢNH, src/shared/kem-anh.ts) — phần đã học, chỉ có khi tham số kem_anh = luon/ti_le; plugin < 0.1.52
  // bỏ qua (bundle khai can_plugin nên Boss không giao cho bot cũ). Runner chọn ảnh từ kho ảnh sản phẩm của Boss, đính vào khung soạn của thao tác
  // gõ thứ `chen_truoc` ngay trước khi gõ, chờ ảnh xem trước, hậu kiểm ảnh trong bình luận vừa đăng.
  kem_anh?: import("./kem-anh").CauHinhKemAnh;
  // ---- 06/10/2026 (bước ĐĂNG VIDEO từ Kho video, src/shared/video-dang.ts) — phần đã học; plugin < PLUGIN_CAN_DANG_VIDEO không hiểu (bundle khai can_plugin nên Boss không giao cho bot cũ).
  // nen_tang: facebook | youtube | tiktok; cau_hinh: chữ "khoá=giá trị;…" (chon, nhan, kieu, che_do, duyet, noi_dung, tran, cho_tu, cho_den, lech…). Bước này KHÔNG có actions: động cơ riêng chạy.
  dang_video?: { nen_tang: string; cau_hinh: string };
  wait_for?: string;
  wait_for_cond?: Cond;
  wait_file?: string;
  on_timeout?: "halt" | "continue";
  // Ghi chú lúc biên dịch (ví dụ: AI chỉ nút nhưng không bấm vì risk high) — hiện ở màn Xem lại.
  compile_note?: string;
}

// Mô tả thao tác bằng tiếng Việt (dùng ở log chạy, màn Xem lại & sửa, phản hồi cho AI).
export function describeAction(a: Action): string {
  switch (a.kind) {
    case "click": return `bấm ${a.target.describe}`;
    case "type": return `gõ "${a.text}" vào ${a.target.describe}${a.enter ? " rồi Enter" : ""}`;
    case "select": return `chọn "${a.value}" ở ${a.target.describe}`;
    case "press": return `nhấn ${a.key}`;
    case "navigate": return `mở ${a.url}`;
    case "scroll": return a.trusted ? `lăn chuột thật ${a.dy ?? 400}px` : "cuộn trang";
  }
}
export function describeActions(actions: Action[]): string { return actions.map(describeAction).join("; "); }

// Điều kiện hậu kiểm bằng tiếng Việt.
export function describeCond(c: Cond): string {
  switch (c.t) {
    case "text_visible": return `thấy chữ '${c.text}'`;
    case "text_gone": return `chữ '${c.text}' biến mất`;
    case "locator_visible": return `thấy ${c.target.describe}`;
    case "locator_gone": return `${c.target.describe} biến mất`;
    case "locator_disabled": return `${c.target.describe} bị mờ`;
    case "dom_exists": return `có phần tử ${c.sel}`;
    case "dom_gone": return `không còn phần tử ${c.sel}`;
    case "url_contains": return `url chứa '${c.s}'`;
    case "value_equals": return `${c.target.describe} = '${c.value}'`;
    case "download": return `tải được file ${c.glob}`;
    case "api": return `API ${c.method} ${c.url}`;
  }
}

export interface BundleInterrupt extends Interrupt {
  see: Cond;
  actions?: Action[];
}

export interface Bundle {
  schema: 1;
  name: string;
  // Mô tả từ kịch bản (kho Boss hiện cho người quản lý); không có thì bỏ trường.
  description?: string;
  version: number;
  script_hash: string;
  compiled_at: string;
  compiled_by: "teach" | "model" | "mixed";
  viewport: Viewport;
  targets: Script["targets"];
  params: Record<string, Param>;
  secrets: string[];
  sources: Script["sources"];
  knowledge: Knowledge;
  interrupts: BundleInterrupt[];
  steps: BundleStep[];
  // Phiên bản plugin thấp nhất chạy được bundle — SUY RA lúc biên dịch (src/shared/phien-ban-plugin.ts), chỉ có khi bundle dùng tính năng mới.
  can_plugin?: string;
}

export interface Heal {
  step_id: string;
  locator_describe: string;
  rung_used: Rung["t"];
  old_ladder: Rung[];
  new_ladder: Rung[];
  at: string;
}

export function needsCompile(step: Step): boolean {
  return step.do !== undefined || step.extract !== undefined || step.paginate !== undefined || step.wait_for !== undefined || step.expect !== undefined;
}

export function stepCompileState(bs: BundleStep): "ok" | "missing" {
  if (bs.do && !bs.actions?.length && !bs.dang_video) return "missing";
  if (bs.extract && !bs.extract_spec) return "missing";
  if (bs.paginate && !bs.paginate.next.ladder.length) return "missing";
  return "ok";
}

export function walkBundleSteps(steps: BundleStep[], visit: (s: BundleStep) => void): void {
  for (const s of steps) {
    visit(s);
    if (s.steps) walkBundleSteps(s.steps, visit);
  }
}

export function collectLocators(step: BundleStep): Locator[] {
  const out: Locator[] = [];
  for (const a of step.actions ?? []) if ("target" in a && a.target) out.push(a.target);
  for (const c of step.postcondition ?? []) if ("target" in c && c.target) out.push(c.target);
  if (step.extract_spec) out.push(step.extract_spec.mode === "list" ? step.extract_spec.list : step.extract_spec.target);
  if (step.paginate) out.push(step.paginate.next);
  return out;
}
